import styled from "styled-components";
import StudentInputs from "./StudentInputs";
import useStudentRegister from "features/teacher/hooks/student/useStudentRegister";
import ParentInputs from "./ParentInputs";
import NormalInputs from "./NormalInputs";
import Modal from "shared/components/Modal";
import { useLocation, useNavigate } from "react-router-dom";
import RegisterFooter from "shared/components/RegisterFooter";
import { hw, scrollbar } from "features/homework/homeworkUi";

const StudentRegister = () => {
    const navigate = useNavigate();
    const id = useLocation().state;
    const {
        studentInputs, onChange, setIsDuplicated, onSaveButtonClick,
        isError, setIsError, isSucceed,
        isOpenPasswordModal, setIsOpenPasswordModal,
        isOpenDuplicatedModal, setIsOpenDuplicatedModal,
        isOpenNullModal, setIsOpenNullModal, isOpenDateModal, setIsOpenDateModal,
        isOpenDeleteSucceedModal, setIsOpenDeleteSucceedModal, onDeleteClickHandler,
    } = useStudentRegister();

    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        onChange({
            target: {
                name: "status",
                value: e.target.value,
            },
        } as React.ChangeEvent<HTMLInputElement>);
    };

    return (
        <Container>
            <Wrapper>
                <Header>
                    <div>
                        <Title>{id ? "학생 수정" : "학생 등록"}</Title>
                        <Desc>
                            {id
                                ? "선택한 원생 정보를 수정한 뒤 저장하세요."
                                : "필수 항목을 입력한 뒤 원생 계정을 생성하세요."}
                        </Desc>
                    </div>
                    <Close type="button" onClick={() => navigate("..")}>닫기</Close>
                </Header>
                <Content>
                    <Modal isOpen={isOpenPasswordModal} title="ERROR" content="패스워드가 일치하지 않습니다" onClose={() => setIsOpenPasswordModal(false)} />
                    <Modal isOpen={isOpenDuplicatedModal} title="ERROR" content="아이디 중복확인을 해주세요" onClose={() => setIsOpenDuplicatedModal(false)} />
                    <Modal isOpen={isOpenNullModal} title="ERROR" content="필수 입력란을 모두 입력해 주세요" onClose={() => setIsOpenNullModal(false)} />
                    <Modal isOpen={isOpenDateModal} title="ERROR" content="날짜 형식이 정확하지 않습니다" onClose={() => setIsOpenDateModal(false)} />
                    <Modal isOpen={isOpenDeleteSucceedModal} title="SUCCESS" content="계정이 삭제되었습니다" onClose={() => { setIsOpenDeleteSucceedModal(false); navigate(-1); }} />
                    <Modal isOpen={isError} title="ERROR" content="계정생성중 오류가 발생하였습니다" onClose={() => setIsError(false)} />
                    <Modal isOpen={isSucceed} title="SUCCESS" content={id ? "수정되었습니다" : "계정이 생성되었습니다"} onClose={() => navigate("..")} />
                    <StudentInputs studentInputs={studentInputs} onChange={onChange} setIsDuplicated={setIsDuplicated} />
                    {id ? (
                        <StatusCard>
                            <StatusTitle>학생 상태</StatusTitle>
                            <StatusSelect value={studentInputs.status} onChange={handleStatusChange}>
                                <option value="active">재학중</option>
                                <option value="suspended">휴학중</option>
                                <option value="graduated">퇴원</option>
                            </StatusSelect>
                        </StatusCard>
                    ) : null}
                    <NormalInputs studentInputs={studentInputs} onChange={onChange} />
                    <ParentInputs studentInputs={studentInputs} onChange={onChange} />
                </Content>
                <RegisterFooter onSaveButtonClick={onSaveButtonClick} onDeleteButtonClick={id ? onDeleteClickHandler : undefined} />
            </Wrapper>
        </Container>
    );
};

const Container = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    padding: 20px 24px;
    z-index: 10;
    background: ${hw.bg};
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 22px 24px 16px;
    border-radius: 16px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    box-shadow: ${hw.shadow};
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    flex-shrink: 0;
`;

const Title = styled.h1`
    margin: 0;
    color: ${hw.text};
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.03em;
`;

const Desc = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`;

const Close = styled.button`
    appearance: none;
    border: 1px solid ${hw.border};
    background: ${hw.surface};
    color: ${hw.textMuted};
    font-size: 13px;
    font-weight: 600;
    border-radius: 8px;
    padding: 8px 12px;
    cursor: pointer;

    &:hover {
        background: ${hw.bg};
        color: ${hw.text};
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    ${scrollbar}
`;

const StatusCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    background: ${hw.bg};
    padding: 20px 22px;
    border-radius: 14px;
    border: 1px solid ${hw.border};
`;

const StatusTitle = styled.h2`
    margin: 0;
    color: ${hw.text};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.02em;
`;

const StatusSelect = styled.select`
    max-width: 240px;
    padding: 10px 12px;
    border: 1px solid ${hw.border};
    border-radius: 8px;
    font-size: 14px;
    color: ${hw.text};
    background: ${hw.surface};
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: ${hw.brand};
        box-shadow: 0 0 0 3px rgba(223, 0, 0, 0.12);
    }
`;

export default StudentRegister;
